import { Box, Button, Dialog, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { useAuthContext } from '@/auth/useAuthContext';
import { useIsAnnouncementBlocking } from '@/components/announcements';
import { AVISO, useAvisoUnico } from '@/components/aviso-unico';
import useNfseImpediments from '@/hooks/useNfseImpediments';
import { PATH_DASHBOARD } from '@/routes/paths';

import { describeImpediment, isKnownImpediment } from './nfseImpedimentCopy';

const TITLE_ID = 'nfse-impediment-title';
const DESCRIPTION_ID = 'nfse-impediment-description';

export const nfseImpedimentSeenKey = (userId) => `cakto:nfse-impediments:visto:${userId}`;

/**
 * Por quanto tempo um motivo já visto deixa de abrir o aviso.
 *
 * Por acesso era demais: o produtor que vende muito via o aviso a cada recarga e
 * a cada login, e aviso repetido assim passa a ser fechado sem leitura (teste
 * manual de 28/10). Mais que um dia deixa vendas demais sem nota antes de o
 * problema ser lembrado. O prazo é decisão de produto: mude aqui.
 */
export const NFSE_IMPEDIMENT_SNOOZE_MS = 24 * 60 * 60 * 1000;

// O localStorage faz o "visto" valer em aba nova, recarga e login novo. Sem ele, o
// sessionStorage segura ao menos a aba; sem os dois, vale o estado da montagem.
const SEEN_STORAGES = [() => window.localStorage, () => window.sessionStorage];

const isSnoozed = (seen, reason, now) =>
  Number.isFinite(seen[reason]) && now - seen[reason] < NFSE_IMPEDIMENT_SNOOZE_MS;

/**
 * `{ [motivo]: quando foi visto, em ms }`. A chave é o motivo, e não a contagem:
 * mais vendas sem nota pelo mesmo motivo não são notícia nova.
 *
 * Ao contrário do `useSeenOnce`, storage ilegível conta como "não viu": lá o
 * erro mais barato é não mostrar uma novidade; aqui é o produtor não saber que as
 * notas pararam (RN09).
 */
const readSeen = (storageKey) => {
  const seen = {};
  if (!storageKey) return seen;

  SEEN_STORAGES.forEach((getStorage) => {
    try {
      const stored = JSON.parse(getStorage().getItem(storageKey));
      if (!stored || typeof stored !== 'object') return;

      Object.entries(stored).forEach(([reason, seenAt]) => {
        if (Number.isFinite(seenAt)) seen[reason] = Math.max(seen[reason] ?? 0, seenAt);
      });
    } catch {
      // Ilegível conta como não visto.
    }
  });

  return seen;
};

const writeSeen = (storageKey, seen) => {
  if (!storageKey) return;

  SEEN_STORAGES.some((getStorage) => {
    try {
      getStorage().setItem(storageKey, JSON.stringify(seen));
      return true;
    } catch {
      return false;
    }
  });
};

// O modal prende o foco, e aberto sobre um formulário engolia o que o produtor
// digitava. Por isso a lista diz onde ele PODE abrir, e não onde não pode: uma
// tela de formulário nova nasce protegida sem ninguém lembrar de excluí-la, e o
// custo de esquecer uma tela de entrada é só o aviso esperar a próxima. Ficam as
// telas onde se chega para olhar, e o início, onde todo login cai: hoje o
// `homeV2`, para onde o `home` redireciona.
const ENTRY_PATHS = [
  PATH_DASHBOARD.general.homeV2,
  PATH_DASHBOARD.general.home,
  PATH_DASHBOARD.general.products.root,
  PATH_DASHBOARD.general.mySales,
  PATH_DASHBOARD.fiscal.financialInvoices,
];

const isEntryPath = (pathname) =>
  ENTRY_PATHS.some((path) => matchPath({ path, end: true }, pathname));

const buttonSx = { textTransform: 'none', minWidth: 'fit-content' };

const salesWithoutInvoiceLabel = (count) =>
  count === 1 ? '1 venda sem nota' : `${count.toLocaleString('pt-BR')} vendas sem nota`;

OtherImpediments.propTypes = {
  impediments: PropTypes.arrayOf(PropTypes.object).isRequired,
  onNavigate: PropTypes.func.isRequired,
};

function OtherImpediments({ impediments, onNavigate }) {
  return (
    <Stack spacing={1.25} component="section" aria-labelledby="nfse-impediment-others">
      <Typography id="nfse-impediment-others" variant="subtitle1" component="h3">
        Também impedem as suas notas
      </Typography>

      <Stack component="ul" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {impediments.map((impediment) => (
          <Stack
            key={impediment.reason}
            component="li"
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            gap={1}
            sx={{ p: 1.5, borderRadius: 1.5, border: 1, borderColor: 'divider' }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {impediment.summary}
              </Typography>
              {impediment.count > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {salesWithoutInvoiceLabel(impediment.count)}
                </Typography>
              )}
            </Box>

            {impediment.action.path && (
              <Button
                variant="text"
                size="small"
                onClick={() => onNavigate(impediment.action.path)}
                sx={buttonSx}
              >
                {impediment.action.label}
              </Button>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

ImpedimentContent.propTypes = {
  main: PropTypes.object.isRequired,
  others: PropTypes.arrayOf(PropTypes.object).isRequired,
  onLater: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

function ImpedimentContent({ main, others, onLater, onNavigate }) {
  const titleRef = useRef(null);

  // O foco vai para o título, e não para o botão principal, para o leitor de tela
  // ler o aviso antes da ação. O efeito do filho roda antes do FocusTrap do Dialog,
  // que então vê o foco já dentro do modal e não o move para o contêiner.
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Ação sem destino é a ciência ("Entendi"); "Resolver depois" ao lado faria o
  // mesmo com outro nome.
  const hasDestination = Boolean(main.action.path);

  return (
    <Stack spacing={2.5}>
      <Box
        aria-hidden
        sx={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          fontSize: 24,
          fontWeight: 800,
          color: `${main.tone}.main`,
          bgcolor: (theme) => alpha(theme.palette[main.tone].main, 0.16),
        }}
      >
        !
      </Box>

      <Stack spacing={1}>
        <Typography
          ref={titleRef}
          id={TITLE_ID}
          tabIndex={-1}
          variant="h4"
          component="h2"
          sx={{ outline: 'none', textWrap: 'balance' }}
        >
          {main.title}
        </Typography>
        <Typography id={DESCRIPTION_ID} variant="body1" color="text.secondary">
          {main.description}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 2,
          py: 1.75,
          borderRadius: 1.5,
          bgcolor: (theme) => alpha(theme.palette[main.tone].main, 0.1),
        }}
      >
        {main.countLabel && (
          <Typography
            component="span"
            sx={{
              fontSize: 32,
              lineHeight: '38px',
              fontWeight: 800,
              // O tom claro some sobre o fundo claro, e o escuro sobre o escuro.
              color: (theme) =>
                theme.palette[main.tone][theme.palette.mode === 'light' ? 'dark' : 'light'],
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {main.countLabel}
          </Typography>
        )}
        <Typography component="span" variant="body2">
          {main.impact}
        </Typography>
      </Stack>

      <Stack spacing={1.25}>
        <Typography variant="subtitle1" component="h3">
          Como resolver
        </Typography>
        <Stack component="ol" spacing={1.25} sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {main.steps.map((step, index) => (
            <Stack key={step} component="li" direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                aria-hidden
                sx={{
                  flex: 'none',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'common.white',
                  bgcolor: 'primary.dark',
                }}
              >
                {index + 1}
              </Box>
              <Typography variant="body2">{step}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {others.length > 0 && <OtherImpediments impediments={others} onNavigate={onNavigate} />}

      <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" gap={1.5}>
        {hasDestination && (
          <Button variant="outlined" color="inherit" onClick={onLater} sx={buttonSx}>
            Resolver depois
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={hasDestination ? () => onNavigate(main.action.path) : onLater}
          sx={buttonSx}
        >
          {main.action.label}
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Este aviso volta amanhã se o problema continuar.
      </Typography>
    </Stack>
  );
}

/**
 * Aviso que interrompe o painel quando a emissão de NFS-e está impedida
 * (CKTMF-532; RN09 e RN10 da CKTMF-522). Banner e sininho não bastam: o produtor
 * precisa dar ciência, então o modal não fecha por clique fora nem por Esc, só
 * pelos botões.
 */
export const NfseImpedimentModal = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuthContext();
  const { impediments, isDecided } = useNfseImpediments();
  // O modal de novidades abre no mesmo momento; um por cima do outro, o produtor
  // fecharia os dois sem ler. O impedimento espera a vez dele.
  const isAnnouncementBlocking = useIsAnnouncementBlocking();
  // O que foi visto nesta montagem: é o que segura o aviso fechado quando o
  // storage falha.
  const [seenHere, setSeenHere] = useState({});

  const storageKey = user?.id ? nfseImpedimentSeenKey(user.id) : null;

  // Motivo que o front ainda não conhece fica de fora: sem texto, o aviso não
  // teria o que dizer nem para onde levar.
  const known = useMemo(
    () => impediments.filter(isKnownImpediment).map(describeImpediment),
    [impediments]
  );

  // Um motivo que ainda não foi visto no prazo reabre o aviso, mesmo com os
  // outros dentro dele.
  const hasUnseen = useMemo(() => {
    const seen = { ...readSeen(storageKey), ...seenHere };
    const now = Date.now();

    return known.some(({ reason }) => !isSnoozed(seen, reason, now));
  }, [known, storageKey, seenHere]);

  const isOpen = !isAnnouncementBlocking && hasUnseen && isEntryPath(pathname);

  // Toda saída do modal passa por aqui, inclusive as ações: quem foi resolver
  // também viu o aviso.
  const acknowledge = () => {
    const now = Date.now();
    const stillSnoozed = Object.fromEntries(
      Object.entries(readSeen(storageKey)).filter(([reason, seenAt]) =>
        isSnoozed({ [reason]: seenAt }, reason, now)
      )
    );
    const seenNow = Object.fromEntries(known.map(({ reason }) => [reason, now]));

    writeSeen(storageKey, { ...stillSnoozed, ...seenNow });
    setSeenHere((previous) => ({ ...previous, ...seenNow }));
  };

  const goTo = (path) => {
    acknowledge();
    navigate(path);
  };

  // Um modal por carregamento, e este tem a vez antes do aviso de recusa da nota avulsa.
  const canOpen = useAvisoUnico(AVISO.impedimentoNfse, {
    decidido: isDecided,
    querAbrir: isOpen,
  });

  if (!canOpen) return null;

  const [main, ...others] = known;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      scroll="body"
      aria-labelledby={TITLE_ID}
      aria-describedby={DESCRIPTION_ID}
      PaperProps={{
        role: 'alertdialog',
        sx: {
          p: { xs: 3, sm: 4.5 },
          borderRadius: 3,
          border: 1,
          borderColor: (theme) => alpha(theme.palette[main.tone].main, 0.45),
        },
      }}
    >
      <ImpedimentContent main={main} others={others} onLater={acknowledge} onNavigate={goTo} />
    </Dialog>
  );
};
